/**
 * Phase 3B: Data Model Types
 * Foundation for Closed-Loop Revenue System
 */

// ============================================================================
// POST-OP STATE MACHINE
// ============================================================================

export type PostOpState = 
  | 'not_started'
  | 'note_uploaded'
  | 'analyzed'
  | 'clarifications_pending'
  | 'reconciled'
  | 'export_ready';

export const POST_OP_STATE_LABELS: Record<PostOpState, string> = {
  not_started: 'Not Started',
  note_uploaded: 'Note Uploaded',
  analyzed: 'Analyzed',
  clarifications_pending: 'Clarifications Pending',
  reconciled: 'Reconciled',
  export_ready: 'Export Ready',
};

export const POST_OP_STATE_ORDER: PostOpState[] = [
  'not_started',
  'note_uploaded',
  'analyzed',
  'clarifications_pending',
  'reconciled',
  'export_ready',
];

/**
 * Valid state transitions for post_op_state
 * Key = current state, Value = valid next states
 */
export const VALID_POST_OP_TRANSITIONS: Record<PostOpState, PostOpState[]> = {
  not_started: ['note_uploaded'],
  note_uploaded: ['analyzed', 'clarifications_pending'],
  analyzed: ['clarifications_pending', 'reconciled'],
  clarifications_pending: ['analyzed', 'reconciled'],
  reconciled: ['export_ready'],
  export_ready: [], // Terminal state
};

// ============================================================================
// OP NOTE SOURCE TYPES
// ============================================================================

export type OpNoteSourceType = 'PASTE' | 'UPLOAD_PDF' | 'UPLOAD_IMAGE' | 'EHR_STUB';

export const OP_NOTE_SOURCE_LABELS: Record<OpNoteSourceType, string> = {
  PASTE: 'Pasted Text',
  UPLOAD_PDF: 'Uploaded PDF',
  UPLOAD_IMAGE: 'Uploaded Image',
  EHR_STUB: 'EHR Import',
};

// ============================================================================
// AUTHORIZATION SCOPE
// ============================================================================

export type AuthorizationScope = 'STANDARD' | 'SUPERSET';

// ============================================================================
// CPT DOCUMENTATION REQUIREMENTS
// ============================================================================

export interface DocumentationElement {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  strictness: 'required' | 'recommended' | 'optional';
}

export interface PayerOverride {
  payer_id: string;
  required_elements_override: DocumentationElement[];
}

export interface CptDocumentationRequirement {
  id: string;
  cpt_code: string;
  required_elements: DocumentationElement[];
  payer_overrides: PayerOverride[];
  version: number;
  effective_date: string;
  is_active: boolean;
  notes?: string;
  updated_by?: string;
  updated_at: string;
  created_at: string;
}

// ============================================================================
// DOCUMENTATION STATUS & EVIDENCE
// ============================================================================

export type DocumentationStatusV2 = 'sufficient' | 'insufficient' | 'ambiguous' | 'not_found';

export const DOCUMENTATION_STATUS_LABELS: Record<DocumentationStatusV2, string> = {
  sufficient: 'Sufficient',
  insufficient: 'Insufficient',
  ambiguous: 'Ambiguous',
  not_found: 'Not Found',
};

export interface EvidenceSnippet {
  text: string;
  offset_start: number;
  offset_end: number;
  matched_keywords: string[];
}

// ============================================================================
// SURGEON CONFIRMATION
// ============================================================================

export type SurgeonConfirmationV2 = 'confirmed_performed' | 'not_performed' | 'not_applicable' | 'deferred';

export const SURGEON_CONFIRMATION_LABELS: Record<SurgeonConfirmationV2, string> = {
  confirmed_performed: 'Confirmed Performed',
  not_performed: 'Not Performed',
  not_applicable: 'Not Applicable',
  deferred: 'Deferred',
};

// ============================================================================
// BILLABLE STATUS
// ============================================================================

export type BillableStatus = 'billable' | 'not_billable' | 'review_required';

export const BILLABLE_STATUS_LABELS: Record<BillableStatus, string> = {
  billable: 'Billable',
  not_billable: 'Not Billable',
  review_required: 'Review Required',
};

/**
 * Standard reason codes for billable status determination
 */
export const BILLABLE_REASON_CODES = {
  NOT_AUTHORIZED: 'CPT was not part of the authorized bundle',
  NO_CONFIRMATION: 'Surgeon confirmation not provided',
  NOT_PERFORMED: 'Surgeon confirmed procedure was not performed',
  MISSING_REQUIREMENT: 'Missing required documentation element',
  AMBIGUOUS_DOCUMENTATION: 'Documentation is ambiguous',
  DEFERRED: 'Confirmation deferred by surgeon',
  CONFIRMED_BILLABLE: 'Surgeon confirmed performed with sufficient documentation',
} as const;

// ============================================================================
// POST-OP RECONCILIATION ITEM (Enhanced CPT Entry)
// ============================================================================

export interface PostOpReconciliationItem {
  id: string;
  case_id: string;
  cpt_code: string;
  cpt_description?: string;
  is_authorized: boolean;
  detected_in_note: boolean;
  evidence_snippets: EvidenceSnippet[];
  documentation_status: DocumentationStatusV2;
  surgeon_confirmation: SurgeonConfirmationV2 | null;
  confirmation_by?: string;
  confirmation_at?: string;
  billable_status: BillableStatus;
  billable_reason_codes: string[];
  is_demo_entry: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// CLARIFICATION PROMPTS
// ============================================================================

export type ClarificationResponse = 'yes' | 'no' | 'na';
export type ClarificationStatus = 'open' | 'resolved' | 'dismissed';
export type ClarificationPromptType = 'YES_NO' | 'SELECT_ONE';

export interface ClarificationPrompt {
  id: string;
  case_id: string;
  cpt_code: string;
  requirement_id?: string;
  prompt_text: string;
  prompt_type: ClarificationPromptType;
  response: ClarificationResponse | null;
  responded_by?: string;
  responded_at?: string;
  status: ClarificationStatus;
  resolution_note?: string;
  is_demo_entry: boolean;
  created_at: string;
  updated_at: string;
}

export const CLARIFICATION_STATUS_LABELS: Record<ClarificationStatus, string> = {
  open: 'Pending Response',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

// ============================================================================
// EXPORT PACKAGES
// ============================================================================

export type ExportFormat = 'epic_copy_paste' | 'pdf_summary' | 'json';

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  epic_copy_paste: 'Epic Copy/Paste',
  pdf_summary: 'PDF Summary',
  json: 'JSON Export',
};

export interface FinalCptLine {
  cpt: string;
  modifiers: string[];
  units: number;
  sequence: number;
}

export interface ModifierLogicEntry {
  cpt: string;
  section: 'primary' | 'secondary';
  modifier_59_applied: boolean;
  rationale: string;
}

export interface ExportPackage {
  id: string;
  case_id: string;
  export_format: ExportFormat;
  final_cpt_lines: FinalCptLine[];
  auth_reference?: string;
  generated_at: string;
  generated_by?: string;
  includes_attestation: boolean;
  includes_timeline: boolean;
  export_hash?: string;
  is_demo_export: boolean;
  created_at: string;
  original_template_version?: number;
  template_id?: string;
  modifier_logic?: ModifierLogicEntry[];
}

// ============================================================================
// CASE WITH PHASE 3 FIELDS
// ============================================================================

export interface Phase3CaseFields {
  authorized_cpts: string[];
  authorization_scope: AuthorizationScope;
  post_op_state: PostOpState;
  op_note_source_type?: OpNoteSourceType;
  original_op_note_text?: string;
  op_note_uploaded_at?: string;
  op_note_uploaded_by?: string;
}

// ============================================================================
// STATE MACHINE HELPERS
// ============================================================================

export function isValidPostOpTransition(from: PostOpState, to: PostOpState): boolean {
  return VALID_POST_OP_TRANSITIONS[from].includes(to);
}

export function getNextValidStates(current: PostOpState): PostOpState[] {
  return VALID_POST_OP_TRANSITIONS[current];
}

export function canExportCase(state: PostOpState, isDemoMode: boolean): boolean {
  // In demo mode, always allow export for demonstration
  if (isDemoMode) return true;
  // In production, only export_ready allows export
  return state === 'export_ready';
}

export function getStateProgress(state: PostOpState): number {
  const index = POST_OP_STATE_ORDER.indexOf(state);
  return ((index + 1) / POST_OP_STATE_ORDER.length) * 100;
}
